import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, CheckCircle2, ArrowRight, Tag, Clock } from "lucide-react";
import { api } from "../lib/api";

const KIT_LABEL = {
  set: "Complete clamp set",
  din3567: "DIN 3567 heavy clamp",
  din3570: "DIN 3570 U-bolt",
  half: "Half pipe clamp",
  other: "Pipe clamp",
};

function useCountdown(endsAtIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!endsAtIso) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const diff = new Date(endsAtIso).getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: false };
}

const CountUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="font-mono text-2xl font-bold tabular-nums text-white sm:text-3xl">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-white/60">{label}</div>
  </div>
);

const SpotlightCard = ({ p, isHero }) => {
  const { t } = useTranslation();
  const showDiscount = p.discount_pct && p.discount_pct > 0;
  return (
    <Link
      to={`/products/${p.id}`}
      data-testid={`weekknaller-card-${p.id}`}
      className={`group relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-zinc-900/40 backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-aramfix-red/60 hover:bg-zinc-900/60 ${
        isHero ? "lg:col-span-2 lg:flex-row" : ""
      }`}
    >
      <div
        className={`relative flex-shrink-0 overflow-hidden bg-white ${
          isHero ? "aspect-[16/10] lg:aspect-auto lg:w-1/2" : "aspect-[4/3]"
        }`}
      >
        {(p.image_url || p.image) ? (
          <img
            src={p.image_url || p.image}
            alt={p.name}
            loading={isHero ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-contain p-6 transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-zinc-300">
            <Tag size={48} />
          </div>
        )}
        {showDiscount && (
          <span className="absolute left-3 top-3 rounded-full bg-aramfix-red px-3 py-1 text-[11px] font-bold tracking-wide text-white shadow-lg">
            −{Math.round(p.discount_pct)}%
          </span>
        )}
        {isHero && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md">
            <Flame size={11} /> {t("weekknaller.spotlight_badge")}
          </span>
        )}
      </div>

      <div className={`flex flex-1 flex-col gap-3 p-5 ${isHero ? "lg:p-8" : ""}`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-aramfix-red">
          {KIT_LABEL[p.kit_type] || KIT_LABEL.other} · {p.material || "316L"}
        </div>
        <h3 className={`font-bold text-white ${isHero ? "text-2xl sm:text-3xl" : "text-base"}`}>
          {p.name_nl || p.name}
        </h3>
        {isHero && p.description && (
          <p className="line-clamp-3 text-sm text-white/70">{p.description}</p>
        )}

        <div className="mt-auto flex items-end justify-between gap-3 pt-2">
          <div>
            {showDiscount && p.was_price_from && (
              <div className="text-xs text-white/40 line-through">
                € {p.was_price_from.toFixed(2)}
              </div>
            )}
            <div className={`font-bold text-white ${isHero ? "text-3xl" : "text-xl"}`}>
              € {(p.price_from || 0).toFixed(2)}
              <span className="ml-1 text-xs font-normal text-white/50">{t("weekknaller.vat_ex")}</span>
            </div>
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400">
              <CheckCircle2 size={11} /> {t("weekknaller.in_stock_badge")}
            </div>
          </div>
          {isHero ? (
            <span
              data-testid="weekknaller-hero-cta"
              className="inline-flex items-center gap-2 rounded-md bg-aramfix-red px-5 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-lg transition group-hover:bg-aramfix-red-hover"
            >
              {t("weekknaller.order_now")} <ArrowRight size={14} />
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/70 group-hover:text-white">
              {t("weekknaller.view")} <ArrowRight size={12} />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function WeekknallerSpotlight() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { d, h, m, s, done } = useCountdown(data?.ends_at);

  useEffect(() => {
    api
      .get("/products/weekknallers")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || !data.enabled || !data.products?.length) return null;

  const [hero, ...rest] = data.products;

  return (
    <section
      className="relative overflow-hidden border-y border-aramfix-red/20"
      style={{
        background:
          "radial-gradient(at 20% 0%, rgba(122, 156, 127, 0.22), transparent 55%), " +
          "radial-gradient(at 90% 100%, rgba(196, 30, 58, 0.18), transparent 55%), " +
          "#0a0a0a",
      }}
      data-testid="weekknaller-spotlight"
    >
      {/* Subtle noise texture for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative mx-auto max-w-[1400px] px-5 py-16 lg:px-10 lg:py-20">
        {/* Header */}
        <div className="mb-10 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-aramfix-red/40 bg-aramfix-red/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-aramfix-red">
              <Flame size={12} /> {t("weekknaller.iso_week_label", { week: data.iso_week })}
            </div>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
              {t("weekknaller.title_main")}
            </h2>
            <p className="mt-3 max-w-xl text-sm text-white/70 sm:text-base">
              {t("weekknaller.subtitle_main")}
            </p>
          </div>

          {/* Countdown */}
          <div
            data-testid="weekknaller-countdown"
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-3 backdrop-blur-md"
          >
            <Clock size={18} className="text-aramfix-red" />
            {done ? (
              <div className="text-sm font-semibold text-white/70">{t("weekknaller.new_selection_loading")}</div>
            ) : (
              <>
                <div className="hidden text-xs font-semibold uppercase tracking-wider text-white/60 sm:block">
                  {t("weekknaller.ends_in_long")}
                </div>
                <div className="flex items-center gap-3">
                  <CountUnit value={d} label={t("weekknaller.unit_days")} />
                  <span className="text-2xl font-bold text-white/30">:</span>
                  <CountUnit value={h} label={t("weekknaller.unit_hours")} />
                  <span className="text-2xl font-bold text-white/30">:</span>
                  <CountUnit value={m} label={t("weekknaller.unit_minutes")} />
                  <span className="text-2xl font-bold text-white/30">:</span>
                  <CountUnit value={s} label={t("weekknaller.unit_seconds")} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Grid: hero card spans 2 cols on lg, rest in 4-up */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          <SpotlightCard p={hero} isHero />
          {rest.slice(0, 4).map((p) => (
            <SpotlightCard key={p.id} p={p} isHero={false} />
          ))}
        </div>
      </div>
    </section>
  );
}
